import {faExclamationCircle} from '@fortawesome/pro-solid-svg-icons';
import clsx from 'clsx';

import {Icon} from '../Icon/Icon';

import type {FontAwesomeIconProps} from '@fortawesome/react-fontawesome';

type Props = React.InputHTMLAttributes<HTMLInputElement> & {
  name: string;
  label?: React.ReactNode;
  description?: React.ReactNode;
  hint?: React.ReactNode;
  error?: string;
  trailingIcon?: FontAwesomeIconProps;
  required?: boolean;
};
export function TextInput({
  label,
  hint,
  description,
  error,
  trailingIcon,
  className,
  ...props
}: Props): React.JSX.Element {
  const hintId = props.name + 'hint';
  const descriptionId = props.name + 'description';
  const errorId = props.name + 'error';

  const describedBy =
    props['aria-describedby'] || !!error
      ? errorId
      : description
        ? descriptionId
        : hintId;

  return (
    <div className={clsx(className, 'max-w-lg')}>
      {label && (
        <div className="mb-2 justify-between sm:flex">
          <label
            htmlFor={props.name}
            className="block text-sm/6 font-medium text-slate12"
          >
            {props.required && (
              <span className="mr-1 text-tomato11">&#42;</span>
            )}
            {label}
          </label>

          {hint && (
            <span id={hintId} className="text-sm/6 text-slate11">
              {hint}
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-1">
        <input
          {...props}
          name={props.name}
          id={props.name}
          aria-describedby={describedBy}
          className={clsx(
            'col-start-1 row-start-1 block w-full rounded-md border-0 bg-slate1 px-3 py-1.5 text-base text-slate12 shadow-xs ring-1 ring-inset focus:outline-0 focus-visible:ring-2 disabled:bg-slate3 sm:text-sm/6',
            error
              ? 'ring-tomato7 focus-visible:ring-tomato8'
              : 'ring-slate7 focus-visible:ring-blue8',
            (trailingIcon || error) && 'sm:pr-9'
          )}
        />
        {(trailingIcon || error) && (
          <Icon
            {...trailingIcon}
            icon={error ? faExclamationCircle : trailingIcon!.icon}
            className={clsx(
              error && 'text-tomato11',
              trailingIcon?.className,
              'pointer-events-none col-start-1 row-start-1 mr-3 size-5 self-center justify-self-end sm:size-4'
            )}
          />
        )}
      </div>

      {(error || description) && (
        <div className="mt-2 text-sm">
          {error ? (
            <p id={errorId} className="font-medium text-tomato11">
              {error}
            </p>
          ) : description ? (
            <p id={descriptionId} className="text-slate11">
              {description}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}
